import { createFeatureSelector, createSelector } from '@ngrx/store';
import { List } from 'immutable';
import { handleActions } from 'redux-actions';
import {
  ActionsEnum,
  BatchMarkNotificationsAsSeen,
  BatchMarkNotificationsAsSeenError,
  DismissNotification,
  DismissNotificationError,
  FetchNotificationsError,
  MarkNotificationAsRead,
  MarkNotificationAsReadError,
  UpdateNotifications,
} from './notification-data.actions';
import { Notification, NotificationStateEnum } from './notification.model';

// feature name
export const FEATURE_NAME = 'notification-data';

// state definition
export interface IState {
  notifications: List<Notification>;
  error: Error;
}

// initial state
export const initialState: IState = {
  notifications: null,
  error: null,
};

// reducer helper functions
export const setInStore = (state: IState, notification: Notification) => {
  const currentNotifications = state.notifications || List([]);
  const index: number = currentNotifications.findIndex(
    (n: Notification) => n.id === notification.id && !n.equals(notification)
  );

  if (index > -1) {
    return {
      ...state,
      notifications: currentNotifications.set(index, notification),
    };
  }

  return {
    ...state,
  };
};

export const batchSetInStore = (
  state: IState,
  notificationIds: List<string>,
  notificationState: NotificationStateEnum
) => {
  if (!notificationIds.isEmpty()) {
    let notifications = state.notifications || List([]);

    notificationIds.forEach((notificationId: string) => {
      const index = notifications.findIndex(
        (n: Notification) => n.id === notificationId
      );

      if (index > -1) {
        notifications = notifications.set(
          index,
          notifications.get(index).setState(notificationState)
        );
      }
    });

    return {
      ...state,
      notifications,
    };
  }

  return {
    ...state,
  };
};

export const deleteFromStore = (
  state: IState,
  notification: Notification
): IState => {
  const currentNotifications = state.notifications || List();
  const newNotifications = currentNotifications.filterNot(
    (e: Notification) => e.id === notification.id
  ) as List<Notification>;

  if (newNotifications.size !== currentNotifications.size) {
    return {
      ...state,
      notifications: newNotifications,
    };
  }

  return {
    ...state,
  };
};

// reducer
const reducerFn = handleActions(
  {
    [ActionsEnum.UPDATE_NOTIFICATIONS]: function (
      state: IState,
      { payload }: UpdateNotifications
    ): IState {
      return {
        ...state,
        notifications: payload.notifications,
      };
    },
    [ActionsEnum.MARK_NOTIFICATION_AS_READ]: function (
      state: IState,
      { payload }: MarkNotificationAsRead
    ): IState {
      const currentNotificationsList = state.notifications;
      const { notification } = payload;

      let currentNotification: Notification = currentNotificationsList.find(
        (n: Notification) => n.id === notification.id
      );

      if (currentNotification && !currentNotification.isRead()) {
        return setInStore(
          state,
          currentNotification.setState(NotificationStateEnum.read)
        );
      }

      return {
        ...state,
      };
    },
    [ActionsEnum.BATCH_MARK_NOTIFICATIONS_AS_SEEN]: function (
      state: IState,
      { payload }: BatchMarkNotificationsAsSeen
    ): IState {
      return batchSetInStore(
        state,
        payload.notificationIds,
        NotificationStateEnum.seen
      );
    },
    [ActionsEnum.DISMISS_NOTIFICATION]: function (
      state: IState,
      { payload }: DismissNotification
    ): IState {
      return deleteFromStore(state, payload.notification);
    },
    [ActionsEnum.FETCH_NOTIFICATIONS_ERROR]: function (
      state: IState,
      { payload }: FetchNotificationsError
    ): IState {
      const { error } = payload;

      return {
        ...state,
        error,
      };
    },
    [ActionsEnum.BATCH_MARK_NOTIFICATIONS_AS_SEEN_ERROR]: function (
      state: IState,
      { payload }: BatchMarkNotificationsAsSeenError
    ): IState {
      const { error } = payload;

      return {
        ...state,
        error,
      };
    },
    [ActionsEnum.MARK_NOTIFICATION_AS_READ_ERROR]: function (
      state: IState,
      { payload }: MarkNotificationAsReadError
    ): IState {
      const { error } = payload;

      return {
        ...state,
        error,
      };
    },
    [ActionsEnum.DISMISS_NOTIFICATION_ERROR]: function (
      state: IState,
      { payload }: DismissNotificationError
    ): IState {
      const { error } = payload;

      return {
        ...state,
        error,
      };
    },
  } as any,
  initialState
);

// required for AOT
export function reducer(state: IState = initialState, action: any) {
  return reducerFn(state, action);
}

// selectors
const selectFeature = createFeatureSelector<IState>(FEATURE_NAME);

export const getNotifications = createSelector(
  selectFeature,
  (state: IState) => state.notifications
);

export const getError = createSelector(
  selectFeature,
  (state: IState) => state.error
);
